import ModalContext from 'contexts/ModalContext'
import { useCallback, useContext, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { useInView } from 'react-intersection-observer'
import { trackEvent } from 'analytics/analytics'
import { promotionClickEvent, promotionViewEvent } from 'analytics/snowplow/events'
import TextButton from 'LuxKit/Button/TextButton'
import noop from 'lib/function/noop'
import SponsoredSearchTileModal from 'sponsoredSearch/SponsoredSearchTileModal'
import _SearchTileBase from '../_SearchTileBase'
import _SearchTileHero from '../_SearchTileHero'
import _SponsoredTileBadge from './_SponsoredTileBadge'
import _SponsoredTileContent from './_SponsoredTileContent'
import _SponsoredTileContentPrimary from './_SponsoredTileContentPrimary'

const CtaButton = styled(TextButton)`
  --button-background: ${p => p.theme.palette.neutral.surface.darkBackground};
  --button-contrast: ${p => p.theme.palette.neutral.surface.darkContrast};
  min-width: ${rem(200)};
  height: ${rem(40)};
`

interface Props {
  media: App.Image
  logo?: App.Image
  eagerLoadFirstImage?: boolean
  onView?: () => void
}

const SPONSORED_TILE_PROMOTION_CONTEXT = {
  promotionType: 'banner' as const,
  promotionName: 'sponsored-search-tile',
}

function SponsoredSearchTile({ media, logo, eagerLoadFirstImage, onView = noop }: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const hasFiredViewRef = useRef(false)
  const [inViewRef, inView] = useInView({
    triggerOnce: true,
    threshold: 0.5,
  })

  useEffect(() => {
    if (inView && !hasFiredViewRef.current) {
      hasFiredViewRef.current = true
      trackEvent(promotionViewEvent({
        promotionContext: SPONSORED_TILE_PROMOTION_CONTEXT,
      }))
      onView()
    }
  }, [inView, onView])

  const handleCtaClick = useCallback(() => {
    trackEvent(promotionClickEvent({
      promotionContext: SPONSORED_TILE_PROMOTION_CONTEXT,
    }))
    showModal(<SponsoredSearchTileModal />)
  }, [showModal])

  return <_SearchTileBase ref={inViewRef} fitContent>
    <_SearchTileHero
      images={[media]}
      eagerLoadFirstImage={eagerLoadFirstImage}
      productLabel={<_SponsoredTileBadge>
        {intl.formatMessage({
          id: 'G8ZUn6JftGIeKaaiCOee',
          defaultMessage: 'Ad',
          description: 'Badge label shown on the sponsored search tile to disclose that the tile is paid advertising content',
        })}
      </_SponsoredTileBadge>}
    />
    <_SponsoredTileContent
      primary={<_SponsoredTileContentPrimary
        title={intl.formatMessage({
          id: 'lF6JMPglLw9SH9jStyHn',
          defaultMessage: 'Earn Triple Société Points with PayPal',
          description: 'Title of the sponsored search tile promoting triple loyalty points when paying with PayPal',
        })}
        body={intl.formatMessage({
          id: 'UOSe11-X13yit64sBOdJ',
          defaultMessage: 'Plus, go in the draw to win 500,000 Société Points. T&Cs apply.',
          description: 'Body copy of the sponsored search tile describing the bonus prize draw entry for the PayPal triple points offer',
        })}
        logo={logo}
      />}
      action={<CtaButton kind="primary" onClick={handleCtaClick}>
        {intl.formatMessage({
          id: '_aYUBvpNckpBXkgPRHXX',
          defaultMessage: 'Learn more',
          description: 'Call-to-action button label on the sponsored search tile that opens a modal with the full offer terms',
        })}
      </CtaButton>}
    />
  </_SearchTileBase>
}

export default SponsoredSearchTile
